import React from 'react';
import propTypes from 'prop-types';
import {
    Button,
    Form,
    Modal
} from 'shared-ui';
import { useCreateVarietalType } from 'data/varietal';
import styles from '../VarietalTypeScreen.module.scss';

type Props = {
    className?: string;
    onClose?: () => void;
    onCompletion?: (varietalTypeId: number) => void;
};

const CreateVarietalTypeModal: React.FC<Props> = ({
    className,
    onClose,
    onCompletion
}: Props) => {
    const { mutateAsync: createType, isLoading: isCreating } = useCreateVarietalType();
    const [labelState, setLabelState] = React.useState<string>('');
    const [codeState, setCodeState] = React.useState<string>('');

    const formIsValid = React.useMemo(() => labelState.trim().length > 0 &&
        codeState.trim().length > 0, [labelState, codeState]);

    const handleLabelChange = React.useCallback((value: string) => {
        setLabelState(value);
    }, []);

    const handleCodeChange = React.useCallback((value: string) => {
        setCodeState(value);
    }, []);

    const handleCloseClick = React.useCallback(() => {
        onClose?.();
    }, [onClose]);

    const handleCreateClick = React.useCallback(async () => {
        if (!formIsValid) return;

        const newTypeId = await createType({ label: labelState, code: codeState });

        onCompletion?.(newTypeId);
        onClose?.();
    }, [labelState, codeState, createType, formIsValid, onClose, onCompletion]);

    return (
        <Modal
            className={`${styles['modal']} ${className} modal-flex`}
            backdrop
            centered
        >
            <Modal.Header>
                <Modal.Title>
                    Créer un cépage
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div>
                    <Form.Group controlId="varietalType-code" className="col">
                        <Form.Label>Code du cépage</Form.Label>
                        <Form.Input
                            value={codeState}
                            maxlength={5}
                            autocomplete="off"
                            readOnly={isCreating}
                            onChange={handleCodeChange}
                        />
                    </Form.Group>
                    <Form.Group controlId="varietalType-label" className="col">
                        <Form.Label>Nom du cépage</Form.Label>
                        <Form.Input
                            value={labelState}
                            maxlength={50}
                            autocomplete="off"
                            readOnly={isCreating}
                            onChange={handleLabelChange}
                        />
                    </Form.Group>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    disabled={isCreating}
                    onClick={handleCloseClick}
                >
                    Annuler
                </Button>
                <Button
                    color="primary"
                    disabled={!formIsValid}
                    pending={isCreating}
                    onClick={handleCreateClick}
                >
                    Créer le cépage
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

CreateVarietalTypeModal.propTypes = {
    className: propTypes.string,
    onClose: propTypes.func,
    onCompletion: propTypes.func
};

CreateVarietalTypeModal.defaultProps = {
    className: undefined,
    onClose: undefined,
    onCompletion: undefined
};

export default CreateVarietalTypeModal;
